import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { fi, sv, enUS } from 'date-fns/locale'
import { store, useAppSelector } from './store'
import { lightTheme, darkTheme } from './assets/styles/theme'
import { selectTheme } from './features/ui/uiSlice'
import { selectIsAuthenticated } from './features/auth/authSlice'
import NotificationSystem from './components/NotificationSystem'
import Loading from './components/Loading'
import ErrorBoundary, { ErrorBoundaryTrigger } from './components/ErrorBoundary'
import UserLayout from './layouts/UserLayout'
import { setupTokenRefresh } from './libs/tokenRefresh'
import { queryClient } from './api/queryClient'
import { useTranslation } from 'react-i18next'
import './i18n' // Initialize i18n

// Initialize token refresh
setupTokenRefresh()

// Lazy load pages (replaces react-loadable)
const Login = lazy(() => import('./pages/Auth/Login'))
const Logout = lazy(() => import('./pages/Auth/Logout'))
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'))
const MaintenanceList = lazy(() => import('./pages/Maintenance/MaintenanceList'))
const MaintenanceDetail = lazy(() => import('./pages/Maintenance/MaintenanceDetail'))
const Companies = lazy(() => import('./pages/Company/Companies'))
const CompanyDetail = lazy(() => import('./pages/Company/CompanyDetail'))
const Settings = lazy(() => import('./pages/Settings/Settings'))
const MaintenanceHistory = lazy(() => import('./pages/Maintenance/MaintenanceHistory'))
const OperationalLog = lazy(() => import('./pages/OperationalLog/OperationalLog'))
const PersonnelManagement = lazy(() => import('./pages/Company/PersonnelManagement'))
const Meters = lazy(() => import('./pages/Company/Meters'))
const MeterDetail = lazy(() => import('./pages/Company/MeterDetail'))
const NewOperationalLog = lazy(() => import('./pages/OperationalLog/NewOperationalLog'))
const InvitationHistory = lazy(() => import('./pages/Company/InvitationHistory'))
const NewMaintenanceRequest = lazy(() => import('./pages/NewMaintenanceRequest/NewMaintenanceRequest'))
const ResidentSearchPage = lazy(() => import('./pages/ResidentSearch/ResidentSearch'))
const CompanyTechnicalDetails = lazy(() => import('./pages/Company/CompanyTechnicalDetails'))

/**
 * Protected route component with UserLayout wrapper
 * Redirects to login if not authenticated
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const location = useLocation()

  if (!isAuthenticated) {
    // Store current path for redirect after login
    // Security: location.pathname is always relative (controlled by React Router)
    const redirect = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/?redirect=${redirect}`} replace />
  }

  return <UserLayout>{children}</UserLayout>
}

/**
 * Main App component with routing
 */
const AppContent: React.FC = () => {
  const theme = useAppSelector(selectTheme)
  const { t, i18n } = useTranslation()

  const selectedTheme = theme === 'dark' ? darkTheme : lightTheme

  // Get date-fns locale based on current language
  const getDateFnsLocale = () => {
    const lang = i18n.language
    if (lang === 'fi' || lang === 'fi-FI') return fi
    if (lang === 'sv' || lang === 'sv-SE') return sv
    return enUS
  }

  return (
    <ThemeProvider theme={selectedTheme}>
      <CssBaseline />
      <ErrorBoundaryTrigger />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
        <NotificationSystem />
        <BrowserRouter>
          <Suspense fallback={<Loading fullPage message={t('common.loading')} />}>
            <Routes>
              {/* Public routes */}
              <Route path='/' element={<Login />} />
              <Route path='/logout' element={<Logout />} />

              {/* Protected routes */}
              <Route
                path='/dashboard'
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/maintenance'
                element={
                  <ProtectedRoute>
                    <MaintenanceList />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/maintenance/new'
                element={
                  <ProtectedRoute>
                    <NewMaintenanceRequest />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/maintenance/:id'
                element={
                  <ProtectedRoute>
                    <MaintenanceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies'
                element={
                  <ProtectedRoute>
                    <Companies />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id'
                element={
                  <ProtectedRoute>
                    <CompanyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/history'
                element={
                  <ProtectedRoute>
                    <MaintenanceHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/operationallog'
                element={
                  <ProtectedRoute>
                    <OperationalLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/operationallog/new'
                element={
                  <ProtectedRoute>
                    <NewOperationalLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/personnelmanagement'
                element={
                  <ProtectedRoute>
                    <PersonnelManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/technical'
                element={
                  <ProtectedRoute>
                    <CompanyTechnicalDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/meters'
                element={
                  <ProtectedRoute>
                    <Meters />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/companies/:id/meters/:meterid'
                element={
                  <ProtectedRoute>
                    <MeterDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/resident-search'
                element={
                  <ProtectedRoute>
                    <ResidentSearchPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/settings'
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/invitations'
                element={
                  <ProtectedRoute>
                    <InvitationHistory />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to login */}
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

/**
 * Root App component
 * Wraps everything with Redux Provider
 */
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Provider>
  )
}

export default App
